// /claims — the released Ectoin claims (Spec docs/spec-evidence-board-v2.md §4b, P3b).
//
// Reads ONLY the released consumer feed (GET /api/evi-claims → app/services/evi_feed.py):
// candidates released on the Evidence board, card SVG inline, grouped by effect area.
// The old per-claim status workflow (approve/reject/reset on evi_consumer_claim) is retired —
// releasing or withdrawing a card happens on the Evidence board (one source, one gate).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, Images, Loader2, RefreshCcw } from "lucide-react";
import clsx from "clsx";
import { api } from "../api";
import { EmptyState } from "../ui";
import { ReleasedClaimTile, groupByArea, type ReleasedClaim } from "./releasedClaims";

export default function EviClaimReviewPage() {
  const [claims, setClaims] = useState<ReleasedClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      setClaims(await api.get<ReleasedClaim[]>("/api/evi-claims"));
    } catch (e) {
      console.error("load released claims failed", e);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => groupByArea(claims), [claims]);

  return (
    <div className="mx-auto max-w-6xl p-4 sm:p-6" data-released-page>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-text">Released claims — Ectoin®</h1>
          <p className="mt-1 max-w-[70ch] text-sm text-text-2">
            {claims.length} cards released on the Evidence board. Only released cards appear here — drop and
            safety cards never do. Release or withdraw a card on the Evidence board.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/blog/dashboard?tab=evidence"
            className="inline-flex items-center gap-1.5 rounded-control border border-border px-3 py-1.5 text-sm text-text-2 hover:bg-surface-3"
          >
            <ClipboardCheck className="h-4 w-4" /> Evidence board
          </Link>
          <Link
            to="/claims/deck"
            className="inline-flex items-center gap-1.5 rounded-control border border-border px-3 py-1.5 text-sm text-text-2 hover:bg-surface-3"
          >
            <Images className="h-4 w-4" /> Presentation
          </Link>
          <button
            onClick={load}
            disabled={loading}
            className="rounded-control p-2 text-text-3 hover:bg-surface-3"
            title="Reload"
            aria-label="Reload"
          >
            <RefreshCcw className={clsx("h-4 w-4", loading && "animate-spin")} />
          </button>
        </div>
      </div>

      {loading && claims.length === 0 ? (
        <div className="flex items-center gap-2 text-text-3">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading released claims …
        </div>
      ) : failed ? (
        <EmptyState icon={<ClipboardCheck className="h-6 w-6" />} title="Could not load the released claims" hint="Reload the page." />
      ) : claims.length === 0 ? (
        <EmptyState
          icon={<ClipboardCheck className="h-6 w-6" />}
          title="No released claims"
          hint="Release a card on the Evidence board to show it here."
        />
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.area} data-released-group={g.area}>
              <div className="mb-3 flex items-baseline gap-2 border-b border-border pb-2">
                <h2 className="font-display text-base font-semibold text-text">{g.area}</h2>
                <span className="font-numeric text-sm tabular-nums text-text-3">{g.items.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {g.items.map((c) => (
                  <ReleasedClaimTile key={c.claim_id} claim={c} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
